import { t } from 'i18next'
import { BlocksView } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/blocks.template'
import { useInView } from '@shared/Frontend/hooks/useInView'
import { cn } from '@shared/Frontend/utils/addToClassName'
import { capitalize } from '@shared/Frontend/utils/capitalize'
import { Clickable } from '@shared/Frontend/utils/clickable'
import { FormControls } from '@shared/Frontend/forms'
import React from 'react'
import { BlockHeader } from '../shared/blockHeader'
import {
  BmiCategory,
  bmiCategoryColor,
  calculateBmi,
  getBmiCategory,
  getWaistCategory,
  isWaistCircumferenceApplicable,
  WaistCategory,
} from './bmiCalculator.calculations'

export const BmiCalculatorBlock: BlocksView['BmiCalculatorBlock'] = (props) => {
  const { id } = props
  const { inView, ref } = useInView({ threshold: 0.1 })

  return (
    <section id={id} ref={ref} className={cn('block block--bmi ls-spacing', inView && 'inview')}>
      <div className="block__inner ls-container ls-gutter">
        <BlockHeader {...props} />
        <div className="block__content">
          <BmiCalculator />
        </div>
      </div>
    </section>
  )
}

const bmiCategoryKeys = ['underweight', 'healthy', 'overweight', 'obese'] as const
const waistCategoryKeys = ['healthy', 'increased', 'tooHigh'] as const

const BmiCalculator = () => {
  const [gender, setGender] = React.useState('')
  const [age, setAge] = React.useState('')
  const [length, setLength] = React.useState('')
  const [weight, setWeight] = React.useState('')
  const [waistCircumference, setWaistCircumference] = React.useState('')
  const [result, setResult] = React.useState<{
    value: number
    category: BmiCategory
    categoryKey: (typeof bmiCategoryKeys)[number]
    waistCategoryKey?: (typeof waistCategoryKeys)[number]
  } | null>(null)

  const ageValue = parseFloat(age)
  const lengthValue = parseFloat(length)
  const weightValue = parseFloat(weight)
  const waistCircumferenceValue = parseFloat(waistCircumference)

  const isFormValid =
    !!gender && ageValue > 0 && lengthValue > 0 && weightValue > 0

  const handleCalculate = () => {
    if (!isFormValid) return

    const bmiValue = calculateBmi(lengthValue, weightValue)
    const category = getBmiCategory(bmiValue, ageValue, gender)

    let waistCategoryKey: (typeof waistCategoryKeys)[number] | undefined
    if (waistCircumferenceValue > 0 && isWaistCircumferenceApplicable(ageValue)) {
      const waistCategory: WaistCategory = getWaistCategory(waistCircumferenceValue, gender)
      waistCategoryKey = waistCategoryKeys[waistCategory]
    }

    setResult({ value: bmiValue, category, categoryKey: bmiCategoryKeys[category], waistCategoryKey })
  }

  return (
    <div className="bmi">
      <div className="bmi__inner">
        <div className="bmi__form">
          <div className="bmi__input">
            <FormControls.FormRadioInGroup
              key="bmi-gender"
              controlId="bmi-gender"
              label={t<string>('bmiCalculator:gender')}
              disabled={false}
              required={true}
              invalid={false}
              errorMessage={undefined}
              fieldMessage={undefined}
              items={[
                { value: 'man', label: t<string>('bmiCalculator:genderMan') },
                { value: 'vrouw', label: t<string>('bmiCalculator:genderWoman') },
              ].map((item) => ({
                controlId: `bmi-gender-${capitalize(item.value)}`,
                label: item.label,
                checked: gender == item.value,
                onChange: () => setGender(item.value),
              }))}
            />
          </div>

          <div className="bmi__input">
            <FormControls.FormItemInGroup
              key="bmi-age"
              type="number"
              controlId="bmi-age"
              inputMode="numeric"
              label={t<string>('bmiCalculator:age')}
              disabled={false}
              required={true}
              min="0"
              placeholder=""
              value={age}
              onChange={(value) => setAge(value)}
              invalid={false}
              errorMessage={undefined}
              fieldMessage={undefined}
            />
            <span className="bmi__suffix">{t<string>('bmiCalculator:ageUnit')}</span>
          </div>

          <div className="bmi__input">
            <FormControls.FormItemInGroup
              key="bmi-length"
              type="number"
              controlId="bmi-length"
              inputMode="numeric"
              label={t<string>('bmiCalculator:length')}
              disabled={false}
              required={true}
              min="0"
              placeholder=""
              value={length}
              onChange={(value) => setLength(value)}
              invalid={false}
              errorMessage={undefined}
              fieldMessage={undefined}
            />
            <span className="bmi__suffix">{t<string>('bmiCalculator:lengthUnit')}</span>
          </div>

          <div className="bmi__input">
            <FormControls.FormItemInGroup
              key="bmi-weight"
              type="number"
              controlId="bmi-weight"
              inputMode="numeric"
              label={t<string>('bmiCalculator:weight')}
              disabled={false}
              required={true}
              min="0"
              placeholder=""
              value={weight}
              onChange={(value) => setWeight(value)}
              invalid={false}
              errorMessage={undefined}
              fieldMessage={undefined}
            />
            <span className="bmi__suffix">{t<string>('bmiCalculator:weightUnit')}</span>
          </div>

          <div className="bmi__input">
            <FormControls.FormItemInGroup
              key="bmi-waist-circumference"
              type="number"
              controlId="bmi-waist-circumference"
              inputMode="numeric"
              label={t<string>('bmiCalculator:waistCircumference')}
              disabled={false}
              required={false}
              min="0"
              placeholder=""
              value={waistCircumference}
              onChange={(value) => setWaistCircumference(value)}
              invalid={false}
              errorMessage={undefined}
              fieldMessage={undefined}
            />
            <span className="bmi__suffix">{t<string>('bmiCalculator:waistCircumferenceUnit')}</span>
          </div>

          <div className="bmi__action">
            <Clickable
              elementKind="button"
              styleKind="button"
              onClick={handleCalculate}
              disabled={!isFormValid}
              label={t<string>('bmiCalculator:calculate')}
              className="button"
              icon="icon-chevron-right"
              hasBlob={true}
            />
          </div>
        </div>
      </div>

      {result && (
        <article className="bmi__result">
          <div className={`bmi__result-score ${bmiCategoryColor[result.category]}`} aria-hidden="true">
            {Math.round(result.value)}
          </div>

          <div className="bmi__result-text">
            <h3 className="bmi__result-title title--sm">
              {t<string>(`bmiCalculator:categories.${result.categoryKey}.title`)}
            </h3>
            <p className="bmi__result-label text--lg">
              {result.waistCategoryKey
                ? t<string>(`bmiCalculator:feedbackCombinations.${result.categoryKey}.${result.waistCategoryKey}`)
                : t<string>(`bmiCalculator:categories.${result.categoryKey}.advice`)}
            </p>
            <p className="bmi__result-disclaimer text--sm">{t<string>('bmiCalculator:disclaimer')}</p>
          </div>
        </article>
      )}
    </div>
  )
}
